import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Card, CardTitle, Table, Td, Th } from "@/core/ui";
import { formatDuration } from "@/core/play/time";
import { useSeasonStandings, type Standing } from "./api";

/**
 * Ranks the standings the server returned. The direction is the strategy's, not ours:
 * `sum_of_ranks` is golf-style (lowest wins), `f1_points` is points-style (highest wins).
 */
export function rankStandings(
  standings: Standing[],
  higherIsBetter: boolean,
): { rank: number; standing: Standing }[] {
  const ordered = [...standings].sort((a, b) =>
    higherIsBetter ? b.points - a.points : a.points - b.points,
  );
  return ordered.map((standing, index) => ({ rank: index + 1, standing }));
}

export function SeasonStandingsPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const query = useSeasonStandings(id);

  if (query.isPending) return <p className="bt-page-message">{t("app.loading")}</p>;
  if (query.isError || !query.data) return <Alert tone="danger">{t("app.error_generic")}</Alert>;

  const { season, higher_is_better: higherIsBetter, standings } = query.data;
  const rows = rankStandings(standings, higherIsBetter);
  const slugs = season.puzzle_slugs;

  return (
    <section className="bt-standings">
      <div>
        <Link to="/seasons" className="bt-standings__back">
          {t("seasons.back")}
        </Link>
        <h1 className="bt-standings__title">{season.name}</h1>
      </div>
      <Card>
        <CardTitle>{t("seasons.standings")}</CardTitle>
        <Alert tone="info">
          {t(`seasons.rule.${higherIsBetter ? "highest" : "lowest"}`, {
            scoring: t(`seasons.scoring.${season.scoring}`, { defaultValue: season.scoring }),
          })}
        </Alert>
        {rows.length === 0 ? (
          <p className="bt-standings__empty">{t("seasons.standings_empty")}</p>
        ) : (
          <Table className="bt-standings__table">
            <thead>
              <tr>
                <Th>#</Th>
                <Th>{t("play.participant")}</Th>
                <Th>{t("seasons.points")}</Th>
                <Th>{t("seasons.solved")}</Th>
                <Th>{t("seasons.total_time")}</Th>
                {slugs.map((slug) => (
                  <Th key={slug}>{slug}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ rank, standing }) => (
                <tr key={`${standing.participant}-${standing.is_team}`}>
                  <Td>{rank}</Td>
                  <Td>
                    {standing.participant}
                    {standing.is_team ? (
                      <span className="bt-standings__team-tag">({t("play.team")})</span>
                    ) : null}
                  </Td>
                  <Td className="bt-table__cell--numeric bt-table__cell--duration bt-standings__points">
                    {standing.points}
                  </Td>
                  <Td>{standing.puzzles_solved}</Td>
                  <Td className="bt-table__cell--numeric bt-table__cell--duration">
                    {formatDuration(standing.total_elapsed_ms)}
                  </Td>
                  {slugs.map((slug) => (
                    <Td
                      key={slug}
                      className="bt-table__cell--numeric bt-table__cell--duration bt-standings__per-puzzle"
                    >
                      {standing.per_puzzle[slug] ?? "–"}
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card>
        <CardTitle>{t("seasons.puzzles")}</CardTitle>
        <ul className="bt-standings__puzzle-list">
          {slugs.map((slug) => (
            <li key={slug}>
              <Link to={`/puzzles/${slug}`}>
                <Badge tone="neutral">{slug}</Badge>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
